import { API_BASE_URL } from '@synergy/config';
import { Box, Button, Checkbox, FormControlLabel } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Media } from '@synergy/models';
import { PaloModal } from '@synergy/components-palo';
import { ReactElement, ReactNode, useState } from 'react';
import { tokens } from '@synergy/theme-palo';
import urljoin from 'url-join';

import { AccountPurchase } from '../models/account-purchase';
import { Offer } from '../../../common/models/offer';
import { PriceMatrix } from './price-matrix/price-matrix';

const c = tokens.color;

/** A delivery address with the offers ticked on its card. */
export interface PriceInfoAddress {
    accountPurchase: AccountPurchase;
    offers: Offer[];
}

/** The ticks of each address, by its id, as the price info endpoints take them. */
export const supplierAccountIdsByAddress = (addresses: PriceInfoAddress[]): Record<number, number[]> =>
    Object.fromEntries(
        addresses.map(({ accountPurchase, offers }) => [
            accountPurchase.id,
            offers.map(offer => offer.supplierAccount.id),
        ])
    );

const addressLabel = (accountPurchase: AccountPurchase): string => {
    const address = accountPurchase.deliveryAddress;

    return [address?.title, address && [address.street, address.number].filter(Boolean).join(' '), address?.city]
        .filter(Boolean)
        .join(', ');
};

export interface PriceInfoSendDialogProps {
    accountPurchase: AccountPurchase;
    offers: Offer[];
    currency: string;
    /** The files that go with every price info of the inquiry. */
    customerMedia: Media[];
    /** How many customers the inquiry has; the files go to each of them. */
    customerCount: number;
    /** The member's other delivery addresses a combined price info would add, with their own ticks. */
    otherAddresses: PriceInfoAddress[];
    onClose: () => void;
    onSend: (allDeliveryAddresses: boolean) => void;
}

const Row = ({ label, children }: { label: ReactNode; children: ReactNode }): ReactElement => (
    <Box sx={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '12px', fontSize: '12.5px', py: '3px' }}>
        <Box sx={{ color: c.ink3 }}>{label}</Box>
        <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
);

/**
 * Everything one checks before a price info goes out, in one window: to
 * whom, which offers, what goes with it, and the price info itself. It
 * replaces the preview and the "Bist du sicher?" after it — the check is this
 * window, and "Senden" sends (#2256).
 */
export const PriceInfoSendDialog = ({
    accountPurchase,
    offers,
    currency,
    customerMedia,
    customerCount,
    otherAddresses,
    onClose,
    onSend,
}: PriceInfoSendDialogProps): ReactElement => {
    // Off by default: the document then holds what the screen shows — the
    // address being worked on, with the offers ticked there.
    const [allDeliveryAddresses, setAllDeliveryAddresses] = useState(false);
    const combined = otherAddresses.length > 0 && allDeliveryAddresses;
    const addresses: PriceInfoAddress[] = combined ? [{ accountPurchase, offers }, ...otherAddresses] : [];
    const contact = accountPurchase.contact;
    const email = contact?.email || contact?.emails?.[0]?.email;

    const pdfLink = urljoin(
        API_BASE_URL,
        `/account-purchases/${accountPurchase.id}/pdf-price-matrix?supplierAccountIds=${offers
            .map(offer => offer.supplierAccount.id)
            .join()}` +
            (combined
                ? '&allDeliveryAddresses=1' +
                  otherAddresses
                      .map(
                          ({ accountPurchase: other, offers: ticked }) =>
                              `&supplierAccountIdsByAddress[${other.id}]=` +
                              ticked.map(offer => offer.supplierAccount.id).join()
                      )
                      .join('')
                : '')
    );

    return (
        <PaloModal
            isVisible={true}
            title={<FormattedMessage id="app.inquiries.detail.priceInfo.dialogTitle" />}
            handleClose={onClose}
            width="lg"
            actionChildren={
                <>
                    <Button variant="text" color="inherit" href={pdfLink} target="_blank" sx={{ mr: 'auto' }}>
                        <FormattedMessage id="app.common.showPdf" />
                    </Button>
                    <Button variant="text" color="inherit" onClick={onClose}>
                        <FormattedMessage id="app.common.cancel" />
                    </Button>
                    <Button variant="contained" disabled={!email} onClick={() => onSend(combined)}>
                        <FormattedMessage id="app.common.send" />
                    </Button>
                </>
            }
        >
            <Box sx={{ mb: '12px' }}>
                <Row label={<FormattedMessage id="app.inquiries.detail.priceInfo.to" />}>
                    {contact?.fullName ?? '–'}
                    {email ? (
                        <Box component="span" sx={{ color: c.ink3 }}>
                            {' · '}
                            {email}
                        </Box>
                    ) : (
                        <Box component="span" sx={{ color: c.crit, fontWeight: 600, ml: '8px' }}>
                            <FormattedMessage id="app.inquiries.dispatch.noEmail" />
                        </Box>
                    )}
                    <Box component="span" sx={{ color: c.ink3 }}>
                        {' · '}
                        {accountPurchase.customerAccount?.name}
                    </Box>
                </Row>
                <Row label={<FormattedMessage id="app.inquiries.detail.priceInfo.offers" />}>
                    {combined
                        ? addresses.map(({ accountPurchase: each, offers: ticked }) => (
                              <Box key={each.id}>
                                  <Box component="span" sx={{ color: c.ink3 }}>
                                      {addressLabel(each)}:{' '}
                                  </Box>
                                  {ticked.map(offer => offer.supplierAccount.name).join(' · ')}
                              </Box>
                          ))
                        : offers.map(offer => offer.supplierAccount.name).join(' · ')}
                </Row>
                <Row label={<FormattedMessage id="app.inquiries.detail.priceInfo.with" />}>
                    <FormattedMessage id="app.inquiries.detail.priceInfo.pdf" />
                    {customerMedia.map(media => (
                        <Box key={media.id ?? media.originalName} component="span" sx={{ color: c.ink2 }}>
                            {' · '}
                            {media.title || media.originalName}
                        </Box>
                    ))}
                    {customerCount > 1 && customerMedia.length > 0 && (
                        <Box sx={{ mt: '3px', fontSize: '11.5px', color: c.warnInk }}>
                            <FormattedMessage
                                id="app.attachments.group.customerWithPriceInfo.toAll"
                                values={{ count: customerCount }}
                            />
                        </Box>
                    )}
                </Row>
            </Box>
            {/* Each address with its own ticks and its own best offer, as the
                document has them. */}
            {combined ? (
                addresses.map(({ accountPurchase: each, offers: ticked }) => (
                    <Box key={each.id} sx={{ mb: '14px' }}>
                        <Box sx={{ fontSize: '12.5px', fontWeight: 600, color: c.ink2, mb: '4px' }}>
                            {addressLabel(each)}
                        </Box>
                        <PriceMatrix offers={ticked} currency={currency} />
                    </Box>
                ))
            ) : (
                <PriceMatrix offers={offers} currency={currency} />
            )}
            {otherAddresses.length > 0 && (
                <FormControlLabel
                    sx={{ mt: 2 }}
                    control={
                        <Checkbox
                            checked={allDeliveryAddresses}
                            onChange={(_, checked) => setAllDeliveryAddresses(checked)}
                        />
                    }
                    label={
                        <FormattedMessage
                            id="app.inquiries.detail.priceMatrixAllDeliveryAddresses"
                            values={{ count: otherAddresses.length + 1 }}
                        />
                    }
                />
            )}
        </PaloModal>
    );
};
